import type { Facility } from '../../../shared/types';

// ============================================================================
// NoAccountManagerBanner — Ben's decision "C + A", 2026-09-17
//
// HCRM Fertility went live on 14 September with nobody recorded as owning it
// and stayed that way until 17 September. The app HAD noticed -- it raised a
// High task on 10 September, four days early, with the national account
// manager's name on it -- but a task on a building shows on that building's
// own card and nowhere else, and My Work does not read building tasks at all.
// So the warning existed, in the one place you would only look if you already
// knew.
//
// ⛔ Ben's objection to fixing this on the card alone, and he is right:
// "would not be seen unless someone opens the card." The half that goes and
// finds somebody is the daily notice (facility_no_account_manager, routed to
// the national AM and the COO, retired the moment a manager is recorded). THIS
// half is what that notice lands on, and what makes the record itself honest
// to anybody already standing on it.
//
// Modelled on MissingAddressPrompt below: renders nothing when the answer is
// there, nothing for a building that is Closed or Cancelled, and it opens the
// card's one edit window rather than carrying a second save of its own.
// ============================================================================

export function NoAccountManagerBanner({
  facility,
  onFix,
}: {
  facility: Facility;
  /** Lands on the Account Summary window, the one place an AM is recorded. */
  onFix: () => void;
}) {
  const inScope = facility.status === 'Active' || facility.status === 'Onboarding';
  if (facility.account_manager_eid || !inScope) return null;

  const live = facility.status === 'Active';

  return (
    <div
      role="status"
      className="mt-4 flex flex-col gap-3 rounded-lg border border-rag-red/40 bg-rag-red/10 p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <p className="text-sm font-semibold text-rag-red">Nobody owns this building</p>
        <p className="mt-1 text-xs text-tcs-text-secondary">
          {live
            ? 'It is open and no account manager is recorded, so every ask it raises lands on whoever holds the national account manager position instead of its owner.'
            : 'No account manager is recorded. Whoever is assigned owns the start confirmation, the route and every escalation on it.'}
        </p>
      </div>
      <button
        type="button"
        onClick={onFix}
        className="min-h-[44px] shrink-0 rounded-md bg-tcs-inverse px-4 text-xs font-semibold text-tcs-inverse-ink"
      >
        Assign one
      </button>
    </div>
  );
}
